import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";

import { AddIcon, DeleteIcon, EditIcon, InfoIcon } from "@chakra-ui/icons";
import {
	Alert,
	AlertDescription,
	AlertIcon,
	AlertTitle,
	Box,
	Button,
	ButtonGroup,
	Card,
	CardBody,
	CardHeader,
	Container,
	FormControl,
	HStack,
	Heading,
	IconButton,
	Input,
	Radio,
	RadioGroup,
	Select,
	SimpleGrid,
	Stack,
	Text,
	Textarea,
	Tooltip,
	VStack,
	Wrap,
	WrapItem,
	useDisclosure,
	useToast,
} from "@chakra-ui/react";
import { Select as ChakraSelect } from "chakra-react-select";
import { FiCreditCard, FiFileText, FiInfo, FiMail, FiPhone, FiUser, FiUsers } from "react-icons/fi";

import {
	MODAL_SECTION_SPACING,
	ModalFieldLabel,
	ModalFooterActions,
	PropertyConfirmModal,
	PropertyModalSection,
	PropertyModalShell,
} from "./shared";
import { addAssociationAsync, addContactAsync, removeAssociationAsync, updateAssociationAsync } from "../../redux/user/userSlice";
import { addContactVerifier } from "../../utils/verifier";
import ContactPopover from "../ContactPopover/ContactPopover";
import OwnerPopover from "../ContactPopover/OwnerPopover";

const contactTypes = [
	{ id: "ownerSpouse", label: "Partener proprietar" },
	{ id: "buildingSuper", label: "Administrator bloc" },
	{ id: "headOfTenantAssociation", label: "Sef asociatie de locatari" },
	{ id: "other", label: "Altele" },
];
export default function Contacts({ property, loading }) {
	const isArchived = property.archived;
	const { t } = useTranslation();
	const initialContactInformation = {
		name: "",
		email: "",
		phoneNumber: "",
		phoneNumber2: "",
		propertyId: property.id,
		contactType: "",
	};
	const initialAssociationInformation = {
		name: "",
		email: "",
		phoneNumber: "",
		administratorName: "",
		iban: "",
		description: "",
	};
	const { isOpen, onOpen, onClose } = useDisclosure();
	const { isOpen: isAddAssociationOpen, onOpen: onAddAssociationOpen, onClose: onAddAssociationClose } = useDisclosure();
	const { isOpen: isEditAssociationOpen, onOpen: onEditAssociationOpen, onClose: onEditAssociationClose } = useDisclosure();
	const { isOpen: isDeleteConfirmationOpen, onOpen: onDeleteConfirmationOpen, onClose: onDeleteConfirmationClose } = useDisclosure();
	const [contactInformation, setContactInformation] = useState(initialContactInformation);
	const [associationInformation, setAssociationInformation] = useState(initialAssociationInformation);
	const [editAssociationInformation, setEditAssociationInformation] = useState({ ...property.association });
	const [error, setError] = useState({});
	const user = useSelector((state) => state.user?.user);

	const associations = user.associations;

	const toast = useToast();
	const [addAssociationSource, setAddAssociationSource] = useState("existing");
	const dispatch = useDispatch();
	useEffect(() => {
		console.log(contactInformation);
	}, [contactInformation]);
	const handleClose = () => {
		onClose();
		setError({});
		setContactInformation(initialContactInformation);
	};
	const handleAddAssociationClose = () => {
		onAddAssociationClose();
		setError({});
		setAssociationInformation(initialAssociationInformation);
	};
	const handleEditAssociationClose = () => {
		onEditAssociationClose();
		setError({});
		setAssociationInformation(initialAssociationInformation);
	};
	const handleDelete = () => {
		const payload = { propertyId: property.id, associationId: property.association.id };
		dispatch(removeAssociationAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Contact Added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onDeleteConfirmationClose();
			});
	};
	const saveAssociation = () => {
		const payload = { ...editAssociationInformation, propertyId: property.id };
		console.log(payload);
		const errors = addContactVerifier(payload);
		console.log(errors);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(updateAssociationAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Contact Added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onEditAssociationClose();
				setEditAssociationInformation(initialAssociationInformation);
			});
	};
	const addAssociation = () => {
		const payload = { ...associationInformation, propertyId: property.id };
		console.log(payload);
		const errors = addContactVerifier(payload);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addAssociationAsync(payload))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Contact Added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onAddAssociationClose();
				setContactInformation(initialContactInformation);
			});
	};
	const handleAddAssociationChange = (e) => {
		setAssociationInformation({ ...associationInformation, [e.target.name]: e.target.value });
	};
	const handleEditAssociationChange = (e) => {
		setEditAssociationInformation({ ...editAssociationInformation, [e.target.name]: e.target.value });
	};
	const addContact = () => {
		console.log(contactInformation);
		const errors = addContactVerifier(contactInformation);
		if (Object.entries(errors).length !== 0) {
			setError(errors);
			toast({
				title: "Error encountered.",
				description: "Invalid information",
				status: "error",
				duration: 2000,
				isClosable: true,
			});
			return;
		}
		setError({});
		dispatch(addContactAsync(contactInformation))
			.then((r) => {
				if (r.error?.message)
					toast({
						title: "Error encountered",
						description: r.error.message,
						status: "error",
						duration: 9000,
						isClosable: true,
					});
				else {
					toast({
						title: "Success!",
						description: "Contact Added!",
						status: "success",
						duration: 2000,
						isClosable: true,
					});
				}
			})
			.finally(() => {
				onClose();
				setContactInformation(initialContactInformation);
			});
	};
	const handleChange = (e) => {
		console.log("changeee");
		setContactInformation({ ...contactInformation, [e.target.name]: e.target.value });
	};
	const renderAddContactModal = () => (
		<PropertyModalShell
			isOpen={isOpen}
			onClose={handleClose}
			size="3xl"
			icon={FiUser}
			title={t("property.addContact")}
			subtitle={t("property.addContactSubtitle")}
			footer={
				<ModalFooterActions isLoading={loading} isDisabled={loading} onCancel={handleClose} onConfirm={addContact} confirmLabel={t("misc.save")} />
			}
		>
			<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
				<Alert status="info" borderRadius="md" size="sm">
					<AlertIcon />
					<VStack align="start" spacing={1}>
						<AlertTitle fontSize="sm">{t("property.contactInformation")}</AlertTitle>
						<AlertDescription fontSize="sm">{t("property.contactInformationAlert")}</AlertDescription>
					</VStack>
				</Alert>
				<PropertyModalSection icon={FiInfo} title={t("property.contactDetails")}>
					<VStack spacing={3} align="stretch">
						<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
							<FormControl isInvalid={error.name}>
								<ModalFieldLabel icon={FiUser}>{t("property.name")}</ModalFieldLabel>
								<Input
									size="sm"
									type="text"
									name="name"
									placeholder={t("property.namePlaceholder")}
									value={contactInformation.name}
									onChange={handleChange}
									borderRadius="md"
								/>
							</FormControl>
							<FormControl isInvalid={error.email}>
								<ModalFieldLabel icon={FiMail}>{t("property.email")}</ModalFieldLabel>
								<Input
									size="sm"
									type="email"
									name="email"
									placeholder={t("property.emailPlaceholder")}
									value={contactInformation.email}
									onChange={handleChange}
									borderRadius="md"
								/>
							</FormControl>
							<FormControl isInvalid={error.phoneNumber}>
								<ModalFieldLabel icon={FiPhone}>{t("property.phoneNumber")}</ModalFieldLabel>
								<Input
									size="sm"
									type="tel"
									name="phoneNumber"
									placeholder={t("property.phoneNumberPlaceholder")}
									value={contactInformation.phoneNumber}
									onChange={handleChange}
									borderRadius="md"
								/>
							</FormControl>
							<FormControl isInvalid={error.phoneNumber2}>
								<ModalFieldLabel icon={FiPhone}>{t("property.alternativePhone")}</ModalFieldLabel>
								<Input
									size="sm"
									type="tel"
									name="phoneNumber2"
									placeholder={t("property.phoneNumberPlaceholder")}
									value={contactInformation.phoneNumber2}
									onChange={handleChange}
									borderRadius="md"
								/>
							</FormControl>
						</SimpleGrid>
						<FormControl isInvalid={error.contactType}>
							<ModalFieldLabel icon={FiFileText}>{t("property.contactType")}</ModalFieldLabel>
							<Select
								size="sm"
								name="contactType"
								value={contactInformation.contactType}
								onChange={handleChange}
								placeholder={t("property.selectContactType")}
								borderRadius="md"
							>
								{contactTypes.map((ct) => (
									<option key={ct.id} value={ct.id}>
										{ct.label}
									</option>
								))}
							</Select>
						</FormControl>
					</VStack>
				</PropertyModalSection>
			</VStack>
		</PropertyModalShell>
	);
	const renderAddAssociationModal = () => (
		<PropertyModalShell
			isOpen={isAddAssociationOpen}
			onClose={handleAddAssociationClose}
			size="4xl"
			icon={FiUsers}
			title={t("property.addAssociation")}
			subtitle={t("property.addAssociationSubtitle")}
			footer={
				<ModalFooterActions
					isLoading={loading}
					isDisabled={loading}
					onCancel={handleAddAssociationClose}
					onConfirm={addAssociation}
					confirmLabel={t("misc.save")}
				/>
			}
		>
			<VStack spacing={MODAL_SECTION_SPACING} align="stretch">
				<PropertyModalSection icon={FiInfo} title={t("property.associationSource")}>
					<RadioGroup
						defaultValue={addAssociationSource}
						onChange={(newValue) => {
							setAddAssociationSource(newValue);
							setAssociationInformation(initialAssociationInformation);
						}}
					>
						<Stack direction="row" spacing={4}>
							<Radio value="existing" colorScheme="blue" size="sm">
								{t("property.existingAssociation")}
							</Radio>
							<Radio value="new" colorScheme="blue" size="sm">
								{t("property.newAssociation")}
							</Radio>
						</Stack>
					</RadioGroup>
				</PropertyModalSection>

				{addAssociationSource === "new" ? (
					<PropertyModalSection icon={FiUsers} title={t("property.associationInformation")}>
						<VStack spacing={3} align="stretch">
							<FormControl isInvalid={error.name}>
								<ModalFieldLabel icon={FiUsers}>{t("property.name")}</ModalFieldLabel>
								<Textarea
									size="sm"
									name="name"
									placeholder={t("property.associationNamePlaceholder")}
									value={associationInformation.name}
									onChange={handleAddAssociationChange}
									borderRadius="md"
								/>
							</FormControl>
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								<FormControl isInvalid={error.administratorName}>
									<ModalFieldLabel icon={FiUser}>{t("property.administratorName")}</ModalFieldLabel>
									<Input
										size="sm"
										name="administratorName"
										placeholder={t("property.administratorNamePlaceholder")}
										value={associationInformation.administratorName}
										onChange={handleAddAssociationChange}
										borderRadius="md"
									/>
								</FormControl>
								<FormControl isInvalid={error.email}>
									<ModalFieldLabel icon={FiMail}>{t("property.email")}</ModalFieldLabel>
									<Input
										size="sm"
										type="email"
										name="email"
										placeholder={t("property.emailPlaceholder")}
										value={associationInformation.email}
										onChange={handleAddAssociationChange}
										borderRadius="md"
									/>
								</FormControl>
								<FormControl isInvalid={error.phoneNumber}>
									<ModalFieldLabel icon={FiPhone}>{t("property.phoneNumber")}</ModalFieldLabel>
									<Input
										size="sm"
										type="tel"
										name="phoneNumber"
										placeholder={t("property.phoneNumberPlaceholder")}
										value={associationInformation.phoneNumber}
										onChange={handleAddAssociationChange}
										borderRadius="md"
									/>
								</FormControl>
								<FormControl isInvalid={error.iban}>
									<ModalFieldLabel icon={FiCreditCard}>{t("property.iban")}</ModalFieldLabel>
									<Input
										size="sm"
										name="iban"
										placeholder={t("property.ibanPlaceholder")}
										value={associationInformation.iban}
										onChange={handleAddAssociationChange}
										borderRadius="md"
									/>
								</FormControl>
							</SimpleGrid>
							<FormControl>
								<ModalFieldLabel icon={FiFileText}>{t("property.description")}</ModalFieldLabel>
								<Textarea
									size="sm"
									name="description"
									placeholder={t("property.descriptionPlaceholder")}
									value={associationInformation.description}
									onChange={handleAddAssociationChange}
									borderRadius="md"
									rows={4}
								/>
							</FormControl>
						</VStack>
					</PropertyModalSection>
				) : (
					<PropertyModalSection icon={FiUsers} title={t("property.selectAssociation")}>
						<FormControl>
							<ModalFieldLabel icon={FiUsers}>{t("property.selectAssociation")}</ModalFieldLabel>
							<ChakraSelect
								options={associations.map((a) => ({
									label: a.name,
									value: a,
								}))}
								onChange={(a) => setAssociationInformation(a.value)}
								placeholder={t("property.selectAssociationPlaceholder")}
								size="sm"
								chakraStyles={{
									control: (provided) => ({ ...provided, minHeight: "32px", fontSize: "14px", borderRadius: "6px" }),
								}}
							/>
						</FormControl>
					</PropertyModalSection>
				)}
			</VStack>
		</PropertyModalShell>
	);
	const renderEditAssociationModal = () => (
		<PropertyModalShell
			isOpen={isEditAssociationOpen}
			onClose={handleEditAssociationClose}
			size="4xl"
			icon={FiUsers}
			title={t("property.editAssociation")}
			subtitle={t("property.editAssociationSubtitle")}
			footer={
				<ModalFooterActions
					isLoading={loading}
					isDisabled={loading}
					onCancel={handleEditAssociationClose}
					onConfirm={saveAssociation}
					confirmLabel={t("misc.save")}
				/>
			}
		>
			<PropertyModalSection icon={FiInfo} title={t("property.associationInformation")}>
				<VStack spacing={3} align="stretch">
					<FormControl isInvalid={error.name}>
						<ModalFieldLabel icon={FiUsers}>{t("property.name")}</ModalFieldLabel>
						<Textarea
							size="sm"
							name="name"
							placeholder={t("property.associationNamePlaceholder")}
							value={editAssociationInformation.name}
							onChange={handleEditAssociationChange}
							borderRadius="md"
						/>
					</FormControl>
					<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
						<FormControl isInvalid={error.administratorName}>
							<ModalFieldLabel icon={FiUser}>{t("property.administratorName")}</ModalFieldLabel>
							<Input
								size="sm"
								name="administratorName"
								placeholder={t("property.administratorNamePlaceholder")}
								value={editAssociationInformation.administratorName}
								onChange={handleEditAssociationChange}
								borderRadius="md"
							/>
						</FormControl>
						<FormControl isInvalid={error.email}>
							<ModalFieldLabel icon={FiMail}>{t("property.email")}</ModalFieldLabel>
							<Input
								size="sm"
								type="email"
								name="email"
								placeholder={t("property.emailPlaceholder")}
								value={editAssociationInformation.email}
								onChange={handleEditAssociationChange}
								borderRadius="md"
							/>
						</FormControl>
						<FormControl isInvalid={error.phoneNumber}>
							<ModalFieldLabel icon={FiPhone}>{t("property.phoneNumber")}</ModalFieldLabel>
							<Input
								size="sm"
								type="tel"
								name="phoneNumber"
								placeholder={t("property.phoneNumberPlaceholder")}
								value={editAssociationInformation.phoneNumber}
								onChange={handleEditAssociationChange}
								borderRadius="md"
							/>
						</FormControl>
						<FormControl isInvalid={error.iban}>
							<ModalFieldLabel icon={FiCreditCard}>{t("property.iban")}</ModalFieldLabel>
							<Input
								size="sm"
								name="iban"
								placeholder={t("property.ibanPlaceholder")}
								value={editAssociationInformation.iban}
								onChange={handleEditAssociationChange}
								borderRadius="md"
							/>
						</FormControl>
					</SimpleGrid>
					<FormControl>
						<ModalFieldLabel icon={FiFileText}>{t("property.description")}</ModalFieldLabel>
						<Textarea
							size="sm"
							name="description"
							placeholder={t("property.descriptionPlaceholder")}
							value={editAssociationInformation.description}
							onChange={handleEditAssociationChange}
							borderRadius="md"
							rows={4}
						/>
					</FormControl>
				</VStack>
			</PropertyModalSection>
		</PropertyModalShell>
	);
	const renderDeleteConfirmationModal = () => (
		<PropertyConfirmModal
			isOpen={isDeleteConfirmationOpen}
			onClose={onDeleteConfirmationClose}
			onConfirm={handleDelete}
			isLoading={loading}
			message={t("property.deleteAssociationConfirmation")}
			emphasis={t("property.actionCannotBeUndone")}
		/>
	);
	return (
		<Container maxW="container.xl" py={6}>
			<VStack spacing={6} align="stretch">
				<HStack justify="space-between" align="center">
					<ButtonGroup spacing={4}>
						<Button colorScheme="blue" onClick={onOpen} isDisabled={isArchived} leftIcon={<AddIcon />} size="md" variant="solid">
							{t("property.addContact")}
						</Button>
						<Button
							colorScheme="blue"
							onClick={onAddAssociationOpen}
							isDisabled={property.association?.id || isArchived}
							leftIcon={<AddIcon />}
							size="md"
							variant="outline"
						>
							{t("property.addAssociation")}
						</Button>
					</ButtonGroup>
				</HStack>

				<Card variant="outline" bg="white" shadow="sm">
					<CardHeader>
						<Heading size="md">{t("property.contactList")}</Heading>
					</CardHeader>
					<CardBody>
						<Wrap spacing={4}>
							{property.buildingSuper.email && (
								<WrapItem>
									<ContactPopover contact={property.buildingSuper} property={property} />
								</WrapItem>
							)}
							<WrapItem>
								<OwnerPopover contact={property.owner} />
							</WrapItem>
							{property.contacts.map((c, index) => (
								<WrapItem key={index}>
									<ContactPopover contact={c} property={property} />
								</WrapItem>
							))}
						</Wrap>
					</CardBody>
				</Card>

				<Card variant="outline" bg="white" shadow="sm">
					<CardHeader>
						<HStack justify="space-between">
							<Heading size="md">{t("property.association")}</Heading>
							<ButtonGroup size="sm" variant="ghost">
								<Tooltip label={t("property.editAssociation")}>
									<IconButton
										isDisabled={!property.association?.id}
										icon={<EditIcon />}
										onClick={() => {
											onEditAssociationOpen();
											setEditAssociationInformation({ ...editAssociationInformation, ...property.association });
										}}
									/>
								</Tooltip>
								<Tooltip label={t("property.removeAssociation")}>
									<IconButton isDisabled={!property.association?.id} colorScheme="red" icon={<DeleteIcon />} onClick={onDeleteConfirmationOpen} />
								</Tooltip>
							</ButtonGroup>
						</HStack>
					</CardHeader>
					<CardBody>
						{property.association?.id ? (
							<SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
								<VStack align="stretch" spacing={3}>
									<HStack>
										<Text fontWeight="semibold" width="150px">
											{t("property.name")}:
										</Text>
										<Text>{property.association.name}</Text>
									</HStack>
									<HStack>
										<Text fontWeight="semibold" width="150px">
											{t("property.administratorName")}:
										</Text>
										<Text>{property.association.administratorName}</Text>
									</HStack>
									<HStack>
										<Text fontWeight="semibold" width="150px">
											{t("property.email")}:
										</Text>
										<Text>{property.association.email}</Text>
									</HStack>
								</VStack>
								<VStack align="stretch" spacing={3}>
									<HStack>
										<Text fontWeight="semibold" width="150px">
											{t("property.phoneNumber")}:
										</Text>
										<Text>{property.association.phoneNumber}</Text>
									</HStack>
									<HStack>
										<Text fontWeight="semibold" width="150px">
											{t("property.iban")}:
										</Text>
										<Text>{property.association.iban}</Text>
									</HStack>
									<HStack alignItems="flex-start">
										<Text fontWeight="semibold" width="150px">
											{t("property.description")}:
										</Text>
										<Text>{property.association.description}</Text>
									</HStack>
								</VStack>
							</SimpleGrid>
						) : (
							<Box textAlign="center" py={8}>
								<InfoIcon boxSize={8} color="gray.400" mb={3} />
								<Text color="gray.500" fontSize="md" fontWeight="medium">
									{t("property.noAssociationMessage")}
								</Text>
							</Box>
						)}
					</CardBody>
				</Card>
			</VStack>

			{renderAddContactModal()}
			{renderAddAssociationModal()}
			{renderEditAssociationModal()}
			{renderDeleteConfirmationModal()}
		</Container>
	);
}
